import { Linkedin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { mockTeam } from '../data/mock';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from '../components/ui/button';

const About = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6 px-6 py-3 bg-brand-400/10 rounded-full border border-brand-400/20">
              <p className="text-brand-400 font-bold text-lg">🏆 Sydney's Premier Recruitment Agency</p>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
              About <span className="text-brand-400">Us</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Growth Talent Group is Australia's premier recruitment specialist dedicated exclusively 
              to the property, real estate, and strata management sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-6xl font-black text-white mb-3">500+</div>
              <p className="text-white/95 text-lg">Successful Placements</p>
            </div>
            <div>
              <div className="text-6xl font-black text-white mb-3">150+</div>
              <p className="text-white/95 text-lg">Partner Organisations</p>
            </div>
            <div>
              <div className="text-6xl font-black text-white mb-3">95%</div>
              <p className="text-white/95 text-lg">Client Satisfaction Rate</p>
            </div>
            <div>
              <div className="text-6xl font-black text-white mb-3">56</div>
              <p className="text-white/95 text-lg">Years Combined Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
                Our <span className="text-brand-400">Story</span>
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  Founded with a vision to revolutionise property recruitment, Growth Talent Group has 
                  become the trusted partner for leading organisations across Australia.
                </p>
                <p>
                  With our team bringing over 56 years of combined real estate and recruitment experience, we understand the unique challenges 
                  and opportunities within the property and strata management industry. We're not just 
                  recruiters—we're industry specialists who speak your language.
                </p>
                <p>
                  Our commitment to excellence has helped hundreds of professionals find rewarding careers 
                  and enabled organisations to build high-performing teams that drive business growth.
                </p>
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-brand-400 to-brand-600 border-none shadow-2xl">
                <CardContent className="p-12 text-white">
                  <h3 className="text-3xl font-black mb-10 leading-tight">Our Impact</h3>
                  <div className="space-y-8">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-5xl font-black mb-3">500+</div>
                      <p className="text-lg">Successful Placements</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-5xl font-black mb-3">150+</div>
                      <p className="text-lg">Partner Organisations</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-5xl font-black mb-3">95%</div>
                      <p className="text-lg">Client Satisfaction Rate</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-5xl font-black mb-3">56</div>
                      <p className="text-lg">Years Combined Experience</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-10">
                <h3 className="text-2xl font-black text-brand-400 mb-6 tracking-tight">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  To connect exceptional property professionals with outstanding organisations, 
                  fostering growth and success for both candidates and clients across Australia.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-10">
                <h3 className="text-2xl font-black text-brand-400 mb-6 tracking-tight">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  To be recognised as Australia's most trusted and respected recruitment partner 
                  in the property and strata management sectors.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-10">
                <h3 className="text-2xl font-black text-brand-400 mb-6 tracking-tight">Our Values</h3>
                <ul className="space-y-4 text-gray-600 text-lg">
                  <li className="flex items-start">
                    <span className="text-brand-400 mr-3 font-bold">•</span>
                    <span>Integrity in every interaction</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-400 mr-3 font-bold">•</span>
                    <span>Excellence in service delivery</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-400 mr-3 font-bold">•</span>
                    <span>Commitment to lasting relationships</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Meet Our <span className="text-brand-400">Team</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium leading-relaxed">
              Industry experts dedicated to your recruitment success
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockTeam.map((member) => (
              <Card 
                key={member.id} 
                className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 cursor-pointer"
                onClick={() => setSelectedMember(member)}
              >
                <CardContent className="p-8">
                  <div className="aspect-square rounded-xl overflow-hidden mb-6 bg-gray-100 shadow-md">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-3 leading-tight">{member.name}</h3>
                  <p className="text-brand-400 text-base font-semibold mb-4">{member.position}</p>
                  <p className="text-gray-900 text-lg font-bold mb-6 leading-relaxed">{member.bio}</p>
                  <div className="flex items-center justify-between">
                    <a 
                      href={member.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-brand-400 hover:text-brand-500 transition-colors duration-200 inline-block hover:scale-110 transition-transform"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Linkedin size={24} />
                    </a>
                    <span className="text-sm text-brand-400 font-semibold">Click for details →</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Team Member Modal */}
          <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              {selectedMember && (
                <div>
                  <DialogHeader>
                    <DialogTitle className="text-3xl font-black text-gray-900 mb-6">
                      Team Profile
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Photo and Contact */}
                    <div className="md:col-span-1">
                      <div className="aspect-square rounded-xl overflow-hidden mb-6 shadow-xl">
                        <img 
                          src={selectedMember.image} 
                          alt={selectedMember.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Email</p>
                          <a href={`mailto:${selectedMember.email}`} className="text-brand-400 hover:text-brand-500 font-semibold break-all">
                            {selectedMember.email}
                          </a>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Phone</p>
                          <a href={`tel:${selectedMember.phone}`} className="text-brand-400 hover:text-brand-500 font-bold text-lg">
                            {selectedMember.phone}
                          </a>
                        </div>
                        <div>
                          <a 
                            href={selectedMember.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-brand-400 hover:text-brand-500 font-semibold"
                          >
                            <Linkedin size={20} className="mr-2" />
                            LinkedIn Profile
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Bio and Details */}
                    <div className="md:col-span-2">
                      <h2 className="text-2xl font-black text-gray-900 mb-2">
                        {selectedMember.name}
                      </h2>
                      <p className="text-brand-400 text-lg font-semibold mb-6">
                        {selectedMember.position}
                      </p>
                      
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">About</h3>
                        <p className="text-gray-600 leading-relaxed">
                          {selectedMember.fullBio}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Specialties</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedMember.specialties.map((specialty) => (
                            <span 
                              key={specialty}
                              className="px-4 py-2 bg-brand-50 text-brand-400 rounded-full text-sm font-semibold border border-brand-200"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <Button 
                          onClick={() => window.location.href = `mailto:${selectedMember.email}`}
                          className="bg-brand-400 hover:bg-brand-500 text-white font-bold w-full py-6"
                        >
                          Get in Touch
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Work With <span className="text-brand-400">Us?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            We combine industry expertise, extensive networks, and a genuine commitment to finding 
            the perfect match. Whether you're a candidate seeking your next opportunity or an 
            organisation building your team, we're here to help you grow.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            {[
              'Specialized focus on property and strata sectors',
              'Deep understanding of industry requirements',
              'Extensive candidate database and networks',
              'Personalized, consultative approach',
              'Fast turnaround times without compromising quality',
              'Ongoing support throughout the recruitment process'
            ].map((item) => (
              <div key={item} className="flex items-start">
                <div className="w-2 h-2 bg-brand-400 rounded-full mt-2 mr-4"></div>
                <p className="text-gray-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
